import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SavingsIcon from '@mui/icons-material/Savings';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { buildApiUrl } from '../config/api';
import { schooldetails } from '../redux/schooldetails';
import {
    PLAN_GOLD,
    PLAN_SILVER,
    getPlanId,
    getBillingCycle,
    formatSilverCyclePriceLabel,
    isSilverPlan,
    isSilverPlanAvailable,
} from '../utils/subscriptionPlans';

export default function SubscriptionPlanPicker({ school, schoolID, onUpdated }) {
    const dispatch = useDispatch();
    const admin = useSelector((state) => state.admin) || {};
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState(null);

    const currentPlan = getPlanId(school);
    const billingCycle = getBillingCycle(school);
    const silverAvailable = isSilverPlanAvailable(school, admin);

    const savePlan = async (subscriptionPackage, nextBillingCycle) => {
        if (!silverAvailable && subscriptionPackage === PLAN_SILVER) {
            setError('Silver plan is not available for Liberia schools');
            return;
        }
        if (!school?._id || !schoolID) {
            setError('School session not loaded');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const body = { _id: school._id, schoolID, subscriptionPackage };
            if (nextBillingCycle) body.billingCycle = nextBillingCycle;
            const res = await axios.post(buildApiUrl('/api/schools/set-subscription-plan'), body);
            const updated = res.data && res.data.school;
            if (updated) dispatch(schooldetails({ data: [updated] }));
            if (onUpdated) onUpdated(updated);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Could not save plan');
        } finally {
            setSaving(false);
        }
    };

    const planCard = (planId, title, subtitle, Icon) => {
        const selected = currentPlan === planId;
        return (
            <Box
                key={planId}
                onClick={() => {
                    if (saving || selected) return;
                    void savePlan(planId, planId === PLAN_SILVER ? billingCycle : undefined);
                }}
                sx={{
                    flex: 1,
                    minWidth: 150,
                    p: 2,
                    borderRadius: 2.5,
                    border: '2px solid',
                    borderColor: selected ? '#1760d3' : '#e2e8f0',
                    bgcolor: selected ? '#eff6ff' : '#fff',
                    cursor: saving ? 'wait' : 'pointer',
                    opacity: saving && !selected ? 0.65 : 1,
                    transition: 'all 0.2s ease',
                    boxShadow: selected ? '0 4px 16px rgba(23, 96, 211, 0.15)' : 'none',
                    '&:hover': {
                        borderColor: selected ? '#1760d3' : '#cbd5e1',
                        transform: saving ? 'none' : 'translateY(-1px)',
                    },
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: selected ? '#dbeafe' : '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.25,
                    }}
                >
                    <Icon sx={{ color: selected ? '#1760d3' : '#64748b', fontSize: 22 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: selected ? '#1760d3' : '#334155' }}>
                    {title}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5, lineHeight: 1.4 }}>
                    {subtitle}
                </Typography>
                {selected ? (
                    <Chip label="Selected" size="small" color="primary" sx={{ mt: 1.25, height: 22, fontSize: 11 }} />
                ) : null}
            </Box>
        );
    };

    if (!silverAvailable) {
        return (
            <Box sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="overline" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                    Your plan
                </Typography>
                <Box
                    sx={{
                        mt: 1,
                        p: 2,
                        borderRadius: 2.5,
                        border: '2px solid #1760d3',
                        bgcolor: '#eff6ff',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                    }}
                >
                    <WorkspacePremiumIcon sx={{ color: '#1760d3', fontSize: 32, mt: 0.25 }} />
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1760d3' }}>
                            Gold
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, lineHeight: 1.5 }}>
                            Per active student — pay as you grow.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="overline" sx={{ color: '#94a3b8', fontWeight: 700, display: 'block' }}>
                Choose your plan
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5, mt: 0.5 }}>
                Click a package to switch. Your selection saves automatically.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {planCard(PLAN_GOLD, 'Gold', 'Per active student — pay as you grow', WorkspacePremiumIcon)}
                {planCard(PLAN_SILVER, 'Silver', 'Flat fee + prepaid SMS units', SavingsIcon)}
            </Box>

            {isSilverPlan(school, admin) ? (
                <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Silver billing cycle
                    </Typography>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={billingCycle}
                        onChange={(_, val) => {
                            if (!val || saving || val === billingCycle) return;
                            void savePlan(PLAN_SILVER, val);
                        }}
                        sx={{
                            flexWrap: 'wrap',
                            gap: 0.5,
                            '& .MuiToggleButton-root': {
                                borderRadius: '10px !important',
                                border: '1px solid #e2e8f0 !important',
                                textTransform: 'none',
                                px: 1.5,
                                py: 0.75,
                                fontSize: 12,
                                fontWeight: 600,
                            },
                            '& .Mui-selected': {
                                bgcolor: '#eff6ff !important',
                                color: '#1760d3 !important',
                                borderColor: '#1760d3 !important',
                            },
                        }}
                    >
                        {['monthly', 'termly', 'yearly'].map((cycle) => (
                            <ToggleButton key={cycle} value={cycle} disabled={saving}>
                                {formatSilverCyclePriceLabel(school, cycle)}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            ) : null}

            {error ? (
                <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
                    {error}
                </Typography>
            ) : null}
            {saving ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Saving plan…
                    </Typography>
                </Box>
            ) : null}
        </Box>
    );
}
